"use client";

import { Home01Icon, Refresh01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as Sentry from "@sentry/nextjs";
import type { Route } from "next";
import Link from "next/link";
import { type ReactNode, useEffect, useState } from "react";
import StatusPage from "@/components/routes/status-page";
import { Button } from "@/components/ui/button";

const MAX_RETRIES = 2;

type SegmentErrorProps = {
	error: Error & { digest?: string };
	reset: () => void;
	segment: string;
	title?: string;
	description?: string;
	escapeHref?: Route;
	escapeLabel?: string;
	escapeIcon?: ReactNode;
};

export default function SegmentError({
	error,
	reset,
	segment,
	title = "Something went wrong",
	description = "This section ran into an unexpected error. Try again, or continue from a safe area.",
	escapeHref = "/",
	escapeLabel = "Back to home",
	escapeIcon,
}: SegmentErrorProps) {
	const [retries, setRetries] = useState(0);

	useEffect(() => {
		Sentry.captureException(error, {
			tags: { segment },
			extra: { digest: error.digest },
		});
		console.error(`[${segment}]`, error);
	}, [error, segment]);

	const exhausted = retries >= MAX_RETRIES;

	return (
		<StatusPage
			code="500"
			title={title}
			description={description}
			actions={
				<>
					{!exhausted && (
						<Button
							variant="default"
							size="lg"
							onClick={() => {
								setRetries((r) => r + 1);
								reset();
							}}
							className="w-full sm:w-auto"
						>
							<HugeiconsIcon icon={Refresh01Icon} className="mr-2 h-5 w-5" />
							Try again
						</Button>
					)}
					<Button
						variant={exhausted ? "default" : "outline"}
						size="lg"
						asChild
						className="w-full sm:w-auto"
					>
						<Link href={escapeHref}>
							{escapeIcon ?? (
								<HugeiconsIcon icon={Home01Icon} className="mr-2 h-5 w-5" />
							)}
							{escapeLabel}
						</Link>
					</Button>
				</>
			}
		/>
	);
}
